import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Mail, Phone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Same master monogram used across the entire site — softer champagne
// `#C8A951`, the canonical brand accent. Kept identical to the header and
// the MinimalFooter so spacing, proportions and color match technically.
const FOOTER_MONOGRAM = `${BACKEND_URL}/api/monogram/gold-champagne.svg`;

// Frontend fallback — used only if the CMS call fails. Every value here is
// editable from the Admin dashboard (Event Page / Guest Info) and picked
// up here at page load.
const FALLBACK = {
  date: 'July 31, 2027',
  hashtag: '#GodBlessTheJohnsons',
  email: 'gracemadeus2027@gmail.com',
  phone: '(612) 384-1233',
  phoneHref: 'tel:+16123841233',
  copyright: '© 2027 The Johnsons. All rights reserved.',
  motto: 'Grace Made Us. Purpose Unites Us.',
  scripture: {
    text: '"...At the right time, I, the Lord, will make it happen."',
    reference: '— Isaiah 60:22',
  },
  coupleShort: { bride: 'Mary', groom: 'Arrington' },
};

const Footer = () => {
  const [c, setC] = useState(FALLBACK);

  useEffect(() => {
    let alive = true;
    const fetchContent = async () => {
      try {
        // Fetch both event content (date/hashtag/contact) and hero content
        // (motto) in parallel so the footer motto stays in sync with the
        // Admin → Home Hero CMS without duplicating storage.
        const [eventRes, heroRes] = await Promise.all([
          axios.get(`${API}/event/content`, { params: { _: Date.now() } }),
          axios.get(`${API}/hero/content`,  { params: { _: Date.now() } }).catch(() => null),
        ]);
        if (!alive || !eventRes?.data) return;
        const d = eventRes.data;
        const h = heroRes?.data || {};
        const dateStr = d.date?.full || d.date?.short || FALLBACK.date;
        const contact = d.guestInfo?.contact || {};
        const phone = contact.phone && contact.phone !== 'TBD' ? contact.phone : FALLBACK.phone;
        const email = contact.email && !contact.email.includes('TBD') ? contact.email : FALLBACK.email;
        setC((prev) => ({
          ...prev,
          date: dateStr,
          hashtag: d.hashtag || FALLBACK.hashtag,
          motto: h.motto || FALLBACK.motto,
          coupleShort: { ...FALLBACK.coupleShort, ...(d.shortNames || {}) },
          email,
          phone,
          // Best-effort tel: href — keep +country code if the raw number has one
          phoneHref: phone.startsWith('+') ? `tel:${phone.replace(/\s+/g, '')}` : FALLBACK.phoneHref,
        }));
      } catch { /* keep fallback */ }
    };
    fetchContent();
    const onVis = () => { if (document.visibilityState === 'visible') fetchContent(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; document.removeEventListener('visibilitychange', onVis); };
  }, []);

  const navLinks = [
    { label: 'Our Story', to: '/our-story', id: 'our-story' },
    { label: 'Gallery', to: '/gallery', id: 'gallery' },
    { label: 'Events', to: '/events', id: 'events' },
    { label: 'RSVP', to: '/rsvp', id: 'rsvp' },
    { label: 'Travel', to: '/travel', id: 'travel' },
    { label: 'Guest Book', to: '/guest-book', id: 'guest-book' },
    { label: 'FAQ', to: '/faq', id: 'faq' }
  ];

  return (
    <footer id="footer" className="bg-[#3B2F2F] text-white py-16" data-testid="footer-section">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          {/* Master M&A monogram — the same refined mark used across the
              invitation suite and the top-left navigation. Rendered as SVG
              so it stays razor-sharp on every device pixel ratio. */}
          <img
            src={FOOTER_MONOGRAM}
            alt="Mary & Arrington monogram"
            className="mx-auto h-14 sm:h-16 md:h-20 w-auto mb-4 select-none"
            draggable="false"
            data-testid="footer-monogram"
          />
          {/* Motto — pulled from Hero CMS so admin edits ripple everywhere. */}
          <p
            className="text-white/70 text-xs sm:text-sm tracking-[0.28em] uppercase mb-10"
            data-testid="footer-motto"
          >
            {c.motto}
          </p>

          {/* Names — recurring candlelight motif on the gold ampersand */}
          <h2 className="font-cursive text-4xl sm:text-5xl text-[#C8A951] mb-2" data-testid="footer-couple">
            {c.coupleShort.bride} <span className="footer-ampersand-flame">&amp;</span> {c.coupleShort.groom}
          </h2>
          <p className="text-white/60 text-sm tracking-widest uppercase mb-8" data-testid="footer-date">
            {c.date}
          </p>

          {/* Navigation */}
          <nav className="flex flex-wrap justify-center gap-4 sm:gap-8 mb-12">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                to={link.to}
                className="nav-link text-white/80 hover:text-[#C8A951]"
                data-testid={`footer-nav-${link.id}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Scripture */}
          <div className="max-w-lg mx-auto mb-12">
            <p className="italic text-white/70 leading-relaxed">
              {c.scripture.text}
            </p>
            <p className="text-[#C8A951] text-sm mt-2">{c.scripture.reference}</p>
          </div>

          {/* Hashtag */}
          <div className="mb-12">
            <p className="font-cursive text-3xl text-[#C8A951]" data-testid="footer-hashtag">{c.hashtag}</p>
            <p className="text-white/60 text-sm mt-2">Share your photos using our hashtag!</p>
          </div>

          {/* Contact */}
          <div className="flex flex-col sm:flex-row justify-center gap-6 mb-12">
            <a
              href={`mailto:${c.email}`}
              className="flex items-center justify-center gap-2 text-white/80 hover:text-[#C8A951] transition-colors"
              data-testid="footer-email"
            >
              <Mail className="w-4 h-4" />
              <span className="text-sm">{c.email}</span>
            </a>
            <a
              href={c.phoneHref}
              className="flex items-center justify-center gap-2 text-white/80 hover:text-[#C8A951] transition-colors"
              data-testid="footer-phone"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm">{c.phone}</span>
            </a>
          </div>

          {/* Divider */}
          <div className="gold-divider" />

          {/* Copyright */}
          <div className="flex items-center justify-center gap-2 text-white/40 text-sm">
            <span>Made with</span>
            <Heart className="w-4 h-4 text-[#C8A951]" fill="#C8A951" />
            <span>for {c.coupleShort.bride} & {c.coupleShort.groom}</span>
          </div>
          <p className="text-white/30 text-xs mt-4">
            {c.copyright}
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export { Footer };
